import React from 'react';
import { locations } from '../data/mock';
import { MapPin, Phone, Clock, Star } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_casaloco-flavors/artifacts/mrxtkbpi_Casa%20Loco%20Logo%20%285%29.png";

const HERO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_045563bf-4bdb-48a2-991f-697b1fca894b/artifacts/ejm84o4p_Casa%20loco%20-%201.mp4";

// Fallback image for browsers that don't support video
const FALLBACK_IMAGE = "https://customer-assets.emergentagent.com/job_882d595f-ff74-4a1a-b1f3-db9ebf1484f1/artifacts/gtr04py8_WhatsApp%20Image%202026-03-26%20at%2012.30.52%20%281%29.jpeg";

const DecoStars = () => (
  <span className="inline-flex gap-1 mx-2">
    <Star size={10} fill="#1A1918" stroke="none" />
    <Star size={8} fill="#1A1918" stroke="none" />
    <Star size={10} fill="#1A1918" stroke="none" />
  </span>
);

const HeroSection = () => {
  return (
    <section className="relative min-h-screen overflow-hidden" style={{ maxWidth: '100vw' }} data-testid="hero-section">
      {/* Video Background */}
      <video
        autoPlay
        muted
        loop
        playsInline
        poster={FALLBACK_IMAGE}
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="hero-video"
      >
        <source src={HERO_VIDEO_URL} type="video/mp4" />
      </video>

      <div className="absolute inset-0 bg-black/50 z-10" />

      {/* Content */}
      <div className="relative z-20 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 pt-24 pb-16" style={{ maxWidth: '100vw', overflowX: 'hidden' }}>
        <div className="text-center" style={{ maxWidth: '100%', padding: '0 8px' }}>
          {/* Logo */}
          <div className="flex justify-center mb-6 animate-fadeIn">
            <img src={LOGO_URL} alt="Casa Loco" className="h-28 sm:h-36 lg:h-44 w-auto object-contain drop-shadow-2xl" style={{ maxWidth: '80vw' }} data-testid="hero-logo" />
          </div>

          {/* Decorative stars */}
          <div className="flex items-center justify-center mb-4">
            <DecoStars />
            <DecoStars />
          </div>

          {/* Main Headline */}
          <h1
            className="font-display text-3xl sm:text-5xl lg:text-7xl mb-4 tracking-wide uppercase"
            data-testid="hero-headline"
            style={{
              color: '#F7B638',
              textShadow: '4px 4px 0 #1A1918, -1px -1px 0 #1A1918, 1px -1px 0 #1A1918, -1px 1px 0 #1A1918',
              wordBreak: 'break-word'
            }}
          >
            Casa Loco – The Best<br />Mexican Restaurant in Hyderabad
          </h1>

          <p
            className="font-mono italic text-lg lg:text-xl mb-6 max-w-2xl mx-auto font-bold"
            data-testid="hero-tagline"
            style={{ color: '#FFFFFF', textShadow: '2px 2px 0 rgba(0,0,0,0.8)' }}
          >
            Authentic Mexican, Sized and Spiced for India.
          </p>

          {/* NOW IN INDIA Badge */}
          <div
            className="inline-flex items-center gap-2 text-white px-6 py-3 mb-8"
            data-testid="hero-badge"
            style={{
              backgroundColor: '#EE2535',
              border: '3px solid #F7B638',
              boxShadow: '4px 4px 0 #1A1918'
            }}
          >
            <span className="font-display text-lg tracking-wider">NOW IN INDIA</span>
          </div>

          {/* Location Card */}
          <div
            className="max-w-lg mx-auto p-8"
            data-testid="hero-location-card"
            style={{
              backgroundColor: '#EE2535',
              border: '4px solid #1A1918',
              boxShadow: '6px 6px 0 #1A1918'
            }}
          >
            {locations.map((location) => (
              <div key={location.id} className="text-center">
                <h3 className="font-display text-3xl lg:text-4xl text-white mb-4">
                  {location.name.toUpperCase()}
                </h3>

                <div className="flex items-center justify-center gap-2 text-white/90 mb-2">
                  <MapPin size={16} />
                  <p className="font-mono text-sm lg:text-base">
                    2nd Floor, Radisson Hitec City,<br />
                    Gachibowli, Hyderabad - 500084
                  </p>
                </div>

                <div className="flex items-center justify-center gap-4 text-white/90 mb-6 font-mono text-sm lg:text-base">
                  <span className="flex items-center gap-1">
                    <Phone size={14} />
                    <a href={`tel:${location.phone}`} className="hover:text-[#F7B638] transition-colors">
                      {location.phone}
                    </a>
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {location.hours}
                  </span>
                </div>

                <a
                  href={`/location/${location.name.toLowerCase()}`}
                  className="inline-block bg-[#F7B638] text-[#1A1918] px-8 py-3 font-display tracking-wider text-lg transition-transform hover:translate-x-[-2px] hover:translate-y-[-2px]"
                  data-testid="hero-visit-btn"
                  style={{ border: '2px solid #1A1918', boxShadow: '3px 3px 0 #1A1918' }}
                >
                  VISIT US
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tricolor stripe divider */}
      <div className="absolute bottom-0 left-0 right-0 h-2 flex z-30">
        <div className="flex-1" style={{ backgroundColor: '#EE2535' }} />
        <div className="flex-1" style={{ backgroundColor: '#F7B638' }} />
        <div className="flex-1" style={{ backgroundColor: '#51A262' }} />
        <div className="flex-1" style={{ backgroundColor: '#F7B638' }} />
        <div className="flex-1" style={{ backgroundColor: '#EE2535' }} />
      </div>
    </section>
  );
};

export default HeroSection;